/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useMemo, useEffect, useState } from "react";
import { Group } from "@vx/group";
import { LinePath, Arc } from "@vx/shape";
import { curveNatural } from "@vx/curve";
import { scaleLinear, scaleOrdinal } from "@vx/scale";
import { GridRows, GridColumns } from "@vx/grid";
import { AxisLeft, AxisBottom } from "@vx/axis";
import { Card, Button } from "antd";
import {
  RollbackOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { LegendOrdinal, LegendItem, LegendLabel } from "@vx/legend";
import { useTooltip, TooltipWithBounds } from "@vx/tooltip";
import ArrowObject from "./ArrowObject";
import Car from "./EgoVehicle";

// const fontColor = "rgba(255, 255, 255, 0.85)";

const colors = {
  lka: "#DB5461",
  lane: "black",
};

const ordinalColorScale = scaleOrdinal({
  domain: ["LKA_STATUS"],
  range: ["red"],
});

const factor = 350 / 150;

function Legend({ disabledState, setDisabledState }) {
  return (
    <LegendOrdinal scale={ordinalColorScale}>
      {(labels) => (
        <div
          style={{
            display: "inline-flex",
            flexDirection: "row",
            marginRight: "5px",
            cursor: "pointer",
          }}
        >
          {labels.map((label) => {
            const isDisabled = disabledState[label.text];
            return (
              <LegendItem
                key={`legend-quantile-${label.value}`}
                margin="0 5px"
                onClick={() =>
                  setDisabledState({
                    ...disabledState,
                    [label.text]: !isDisabled,
                  })
                }
              >
                <svg width={15} height={15}>
                  <rect
                    fill={isDisabled ? "transparent" : label.value}
                    stroke={label.value}
                    strokeWidth="2px"
                    width={15}
                    height={15}
                  />
                </svg>
                <LegendLabel align="left" margin="0 0 0 4px">
                  {label.text}
                </LegendLabel>
              </LegendItem>
            );
          })}
        </div>
      )}
    </LegendOrdinal>
  );
}

const margin = { top: 20, right: 50, bottom: 70, left: 70 };

function BirdsEyeView({
  width,
  height,
  data,
  selectedObject,
  setSelectedObject,
}) {
  const [disabledState, setDisabledState] = useState({
    Radar: false,
    Camera: false,
    LKA_STATUS: false,
    FOV: false,
  });
  const [zoom, setZoom] = useState({
    xMin: -20,
    xMax: 20,
    yMin: -60,
    yMax: 180,
  });
  const zoomIn = () => {
    if (zoom.xMin < -5) {
      setZoom({
        xMin: zoom.xMin + 2.5,
        xMax: zoom.xMax - 2.5,
        yMin: zoom.yMin + 10,
        yMax: zoom.yMax - 10,
      });
    }
  };
  const zoomOut = () => {
    if (zoom.xMin > -40) {
      setZoom({
        xMin: zoom.xMin - 2.5,
        xMax: zoom.xMax + 2.5,
        yMin: zoom.yMin - 10,
        yMax: zoom.yMax + 10,
      });
    }
  };
  const rollBack = () => {
    setZoom({
      xMin: -20,
      xMax: 20,
      yMin: -60,
      yMax: 180,
    });
  };
  useEffect(() => {
    const keyDownListener = (e) => {
      if (e.key === "ArrowUp") {
        setSelectedObject((o) => {
          if (!o) {
            return null;
          }
          return {
            index: data[o.type].length - 1 === o.index ? 0 : o.index + 1,
            type: o.type,
          };
        });
      }
      if (e.key === "ArrowDown") {
        setSelectedObject((o) => {
          if (!o) {
            return null;
          }
          return {
            index: o.index === 0 ? data[o.type].length - 1 : o.index - 1,
            type: o.type,
          };
        });
      }
      if (e.key === "Escape") {
        setSelectedObject(null);
      }
    };

    document.addEventListener("keydown", keyDownListener);
    return () => document.removeEventListener("keydown", keyDownListener);
  }, [data]);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const xScale = useMemo(
    () =>
      scaleLinear({
        domain: [zoom.xMin, zoom.xMax],
        range: [0, xMax],
        clamp: true,
      }),
    [xMax, zoom]
  );
  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: [zoom.yMin, zoom.yMax],
        range: [yMax, 0],
        clamp: true,
      }),
    [yMax, zoom]
  );
  if (!data) {
    data = {
      lane: { left: [], right: [] },
      lka: [],
    };
  }

  useEffect(() => {
    if (selectedObject) {
      const object = data[selectedObject.type][selectedObject.index];
      showTooltip({
        tooltipLeft: xScale(object.x) + margin.left,
        tooltipTop: yScale(object.y) + margin.top,
        tooltipData: object,
      });
    }
  }, [selectedObject]);

  return (
    <Card
      title="Bird's Eye View"
      size="small"
      extra={
        <div>
          <Legend
            disabledState={disabledState}
            setDisabledState={setDisabledState}
          />
          <Button style={{ margin: "2px" }} onClick={zoomIn}>
            <PlusOutlined />
          </Button>
          <Button style={{ margin: "2px" }} onClick={zoomOut}>
            <MinusOutlined />
          </Button>
          <Button style={{ margin: "2px" }} onClick={rollBack}>
            <RollbackOutlined />
          </Button>
        </div>
      }
      style={{
        height: "50vh",
        width: "100%",
        overflowY: "hidden",
        overflowX: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <svg width={width} height={height} onMouseOut={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={xMax}
            height={yMax}
            stroke="#e0e0e0"
          />
          <GridColumns
            scale={xScale}
            width={xMax}
            height={yMax}
            stroke="#e0e0e0"
          />

          <line x1={xMax} x2={xMax} y1={0} y2={yMax} stroke="#e0e0e0" />
          <AxisBottom scale={xScale} top={yMax} label="Lateral Distance (m)" />
          <AxisLeft scale={yScale} label="Longitudinal Distance (m)" />

          <LinePath
            curve={curveNatural}
            data={data.lane && data.lane.left}
            x={(d) => xScale(d.x)}
            y={(d) => yScale(d.y)}
            stroke={colors.lane}
            strokeWidth={1.5}
            shapeRendering="geometricPrecision"
          />

          <LinePath
            curve={curveNatural}
            data={data.lane && data.lane.right}
            x={(d) => xScale(d.x)}
            y={(d) => yScale(d.y)}
            stroke={colors.lane}
            strokeWidth={1.5}
            shapeRendering="geometricPrecision"
          />

          {disabledState.FOV ? null : (
            <div>
              <Arc
                innerRadius={0}
                outerRadius={150 * factor}
                startAngle={-1 * Math.PI * 0.25}
                endAngle={Math.PI * 0.25}
                fill={colors.camera}
                opacity={0.2}
                data={[10]}
                transform={`translate(${xScale(0)} ${yScale(0)})`}
              />
              <Arc
                innerRadius={0}
                outerRadius={10 * factor}
                startAngle={(-1 * Math.PI * 45) / 180}
                endAngle={(Math.PI * 45) / 180}
                fill={colors.radar}
                opacity={0.2}
                data={[10]}
                transform={`translate(${xScale(0)} ${yScale(7)})`}
              />
              <Arc
                innerRadius={0}
                outerRadius={60 * factor}
                startAngle={(-1 * Math.PI * 20) / 180}
                endAngle={(Math.PI * 20) / 180}
                fill={colors.radar}
                opacity={0.2}
                data={[10]}
                transform={`translate(${xScale(0)} ${yScale(7)})`}
              />
              <Arc
                innerRadius={0}
                outerRadius={160 * factor}
                startAngle={(-1 * Math.PI * 15) / 180}
                endAngle={(Math.PI * 15) / 180}
                fill={colors.radar}
                opacity={0.2}
                data={[10]}
                transform={`translate(${xScale(0)} ${yScale(7)})`}
              />
            </div>
          )}

          {disabledState.LKA_STATUS
            ? null
            : data.lka.map((fo, index) => {
                if (fo.magnitude !== 0) {
                  const onFocus = () => {
                    showTooltip({
                      tooltipLeft: xScale(fo.x) + margin.left,
                      tooltipTop: yScale(fo.y) + margin.top,
                      tooltipData: fo,
                    });
                  };

                  return (
                    <ArrowObject
                      key={Math.random()}
                      top={yScale(fo.y)}
                      left={xScale(fo.x)}
                      rotation={-1 * fo.rotation + 90}
                      length={fo.magnitude / 300}
                      fill={colors.lka}
                      onMouseOut={hideTooltip}
                      onMouseEnter={onFocus}
                      selectedObject={selectedObject}
                      onClick={() => {
                        setSelectedObject({
                          index,
                          type: "lka",
                        });
                      }}
                    />
                  );
                }
                return null;
              })}
          <Car top={yScale(0)} left={xScale(0)} />
        </Group>
      </svg>
      {tooltipOpen && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
        >
          <strong>
            {tooltipData.name} {tooltipData.id}
          </strong>
          <br />
          Longitudinal: {tooltipData.y}
          <br />
          Lateral: {tooltipData.x}
        </TooltipWithBounds>
      )}
    </Card>
  );
}

export default React.memo(BirdsEyeView);

